import axiosInstance from "./axiosInstance";

export const checkEcu = async ({ file, brand, ecu }) => {
  const formData = new FormData();
  formData.append("file", file);
  if (brand && brand.trim()) formData.append("brand", brand.trim());
  if (ecu && ecu.trim()) formData.append("ecu", ecu.trim());

  const response = await axiosInstance.post("/ecu/check", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const patchEcu = async ({
  file,
  brand,
  ecu,
  swNumber,
  modules = [],
  tuningIds = [],
  dtcPairs = [],
}) => {
  const formData = new FormData();
  formData.append("file", file);
  if (brand && brand.trim()) formData.append("brand", brand.trim());
  if (ecu && ecu.trim()) formData.append("ecu", ecu.trim());
  if (swNumber && swNumber.trim()) formData.append("sw_number", swNumber.trim());

  modules.forEach((name) => formData.append("modules", name));
  tuningIds.forEach((id) => formData.append("tuning_id", id));
  dtcPairs.forEach((pair) => {
    formData.append("dtcs", pair.code1 ?? "");
    formData.append("dtcs2", pair.code2 ?? "");
  });

  const response = await axiosInstance.post("/ecu/patch", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    responseType: "blob",
  });

  let fileName = "patched.bin";
  const disposition = response.headers["content-disposition"];
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match && match.length > 1) fileName = match[1];
  }

  return {
    blob: new Blob([response.data]),
    fileName,
    charged: response.headers["x-credits-charged"],
    balance: response.headers["x-credits-balance"],
  };
};

export const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName || "patched.bin");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const DTC1_KEYS = ["dtcCode", "dtc_code", "dtc1", "DTC1", "code", "Code"];
const DTC2_KEYS = ["dtcCode2", "dtc_code2", "dtc2", "DTC2"];
const DTC_DESC_KEYS = ["description", "dtcDescription", "desc", "text", "name", "fault"];

const pickCell = (row, keys) => {
  for (const key of keys) {
    if (row[key] != null && String(row[key]).trim() !== "") {
      return String(row[key]);
    }
  }
  return "";
};

export const extractDtcPairs = (dtcTable) => {
  if (!Array.isArray(dtcTable)) return [];
  const pairs = [];
  for (const row of dtcTable) {
    if (!row || typeof row !== "object") continue;
    const c1 = pickCell(row, DTC1_KEYS);
    const c2 = pickCell(row, DTC2_KEYS);
    const p1 = c1 ? c1.trim().split(/\s+/) : [];
    const p2 = c2 ? c2.trim().split(/\s+/) : [];
    const count = Math.max(p1.length, p2.length);
    for (let i = 0; i < count; i++) {
      const code1 = p1[i] ?? "";
      const code2 = p2[i] ?? "";
      if (code1 === "" && code2 === "") continue;
      pairs.push({ code1, code2 });
    }
  }
  return pairs;
};

// Splits one display row into {code1,code2} pairs (a cell can pack several
// whitespace-separated codes). Used to send only the DTC rows toggled off.
export const dtcRowToPairs = (row) => {
  if (!row) return [];
  const p1 = row.code1 ? String(row.code1).trim().split(/\s+/) : [];
  const p2 = row.code2 ? String(row.code2).trim().split(/\s+/) : [];
  const count = Math.max(p1.length, p2.length);
  const pairs = [];
  for (let i = 0; i < count; i++) {
    const code1 = p1[i] ?? "";
    const code2 = p2[i] ?? "";
    if (code1 === "" && code2 === "") continue;
    pairs.push({ code1, code2 });
  }
  return pairs;
};

export const extractDtcRows = (dtcTable) => {
  if (!Array.isArray(dtcTable)) return [];
  const rows = [];
  for (const row of dtcTable) {
    if (!row || typeof row !== "object") continue;
    const code1 = pickCell(row, DTC1_KEYS);
    const code2 = pickCell(row, DTC2_KEYS);
    const description = pickCell(row, DTC_DESC_KEYS);
    if (!code1 && !code2 && !description) continue;
    rows.push({ code1, code2, description });
  }
  return rows;
};

export const hasDtc2Column = (rows) => rows.some((row) => row.code2 && row.code2 !== "");

export const extractApiError = async (err, fallback) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed.error || parsed.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.error || data?.message || err?.message || fallback;
};
